import { Box, Step, StepButton, StepLabel, Stepper } from "@mui/material";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import { StepIconProps } from "@mui/material/StepIcon";
import { styled } from "@mui/material/styles";
import { ReactNode } from "react";
import { Link, To } from "react-router";

import { semanticColors } from "@dfp/isomorphic-design-tokens";

export type StepProps = {
  label: ReactNode;
  hidden?: boolean;
  disabled?: boolean;
} & (
  | {
      type: "button";
      onClick?: () => void;
    }
  | {
      type: "link";
      to: To;
    }
  | {
      type?: "default";
    }
);

type Props = {
  steps: StepProps[];
  activeIndex: number;
};

export function HorizontalStepper(props: Props) {
  return (
    <Box sx={{ width: "100%" }}>
      <Stepper alternativeLabel connector={<StyledStepperConnector />}>
        {props.steps.map((s, idx) => {
          const label = (
            <StepLabel
              sx={{
                "& .MuiStepLabel-label.MuiStepLabel-alternativeLabel": {
                  marginTop: "8px",
                  color: semanticColors.text,
                  fontWeight: 600,
                },
              }}
              slots={{
                stepIcon: StyledStepIcon,
              }}
            >
              {s.label}
            </StepLabel>
          );

          return !s.hidden ? (
            <Step
              key={idx}
              active={idx === props.activeIndex}
              completed={idx < props.activeIndex}
              disabled={s.disabled || idx >= props.activeIndex}
            >
              {s.type !== "link" ? (
                <StepButton
                  onClick={s.type === "button" ? s.onClick : undefined}
                >
                  {label}
                </StepButton>
              ) : (
                <StepButton component={Link} to={s.to} replace>
                  {label}
                </StepButton>
              )}
            </Step>
          ) : null;
        })}
      </Stepper>
    </Box>
  );
}

const StyledStepperConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: "calc(-50% + 16px)",
    right: "calc(50% + 16px)",
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: semanticColors.fillBrand,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: semanticColors.iconBrand,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: semanticColors.borderSecondary,
    borderTopWidth: 2,
    borderRadius: 1,
  },
}));

const StyledStepIconRoot = styled("div")<{
  ownerState: { active?: boolean; completed?: boolean };
}>(({ ownerState }) => ({
  color: semanticColors.fillSecondary,
  display: "flex",
  height: 22,
  alignItems: "center",
  ...(ownerState.active && {
    color: semanticColors.fillBrand,
  }),
  ...(ownerState.completed && {
    color: semanticColors.fillBrand,
  }),
  "& .StyledStepIcon-circle": {
    width: 10,
    height: 10,
    borderRadius: "50%",
    backgroundColor: "currentColor",
  },
}));

function StyledStepIcon(props: StepIconProps) {
  const { active, completed, className } = props;

  return (
    <StyledStepIconRoot
      ownerState={{ active, completed }}
      className={className}
    >
      <div className="StyledStepIcon-circle" />
    </StyledStepIconRoot>
  );
}
